import { cloneDeep, isNumber, toInteger } from 'lodash';
import { getFramework } from '@/framework';
import { TextMeasurer } from '@/utils/text-kit';
import {
    MAX_FONT_SIZE,
    MIN_FONT_SIZE,
    TextWritingType,
    type ICorrectLayerMinSize,
    type IFontBackground,
    type IFontGlow,
    type IFontShadow,
    type IFontStroke,
    type ILayerBehavior,
    type ILayerProps,
    type ITextLayerProps,
} from '@/version-types';

type Behaviors = Pick<
    ILayerBehavior,
    | 'onBeforeScaleUpdate'
    | 'onBeforeResizeUpdate'
    | 'onBeforeCrop'
    | 'getActiveBaseInfo'
    | 'getScaleMinRatio'
    | 'getLayerMinSize'
    | 'whenGroupCanResize'
    | 'correctLayerMinSize'
    | 'onBeforeGetLayerWithClone'
    | 'onBaseScaleEnd'
    | 'onCheckCanResize'
>;

export const createOperationBehaviors = (identity: string) => {
    const behaviors: Behaviors = {
        /**
         * 文本图层缩放操作后，对文本图层数据的额外操作
         *
         * ATTENTION: 该方法会改变 mutable 入参
         *
         * @param layer - 图层属性
         * @param options - 配置选项
         */
        onBeforeScaleUpdate(layer: ILayerProps, { correctResult }) {
            const { letterSpacing, textGlow, textShadow, textStroke, textBackground, fontSize } =
                correctResult as ICorrectLayerMinSize;

            const layerData = layer.data as ITextLayerProps;
            const ratio = fontSize / layerData.fontSize;

            layerData.letterSpacing = letterSpacing;
            layerData.fontSize = fontSize;
            layerData.textGlow = textGlow;
            layerData.textShadow = textShadow;
            layerData.textStroke = textStroke;
            layerData.textBackground = textBackground;

            const textContents = layerData.textContents || [];
            if (textContents.length > 0) {
                layerData.textContents = textContents.map((item) => {
                    item.fontSize = item.fontSize * ratio;
                    return item;
                });
            }
        },

        /**
         * 文本图层尺寸调整操作后，根据实际情况（文本内容）返回修正后的图层尺寸
         *
         * ATTENTION: 该方法会改变 mutable 入参
         *
         * @param layer - 图层属性
         * @param extra - 额外属性
         */
        onBeforeResizeUpdate(layer: ILayerProps, { isChangeHeight }) {
            const { width, height } = layer;
            const layerData = layer.data as ITextLayerProps;
            const { fontSize, lineSpacing, listStyle } = layerData;

            let isMin = false;
            let minHeight = 0;
            let minWidth = 0;

            // 改变文本图层高度（只有竖排文字才可以改变高度）
            if (isChangeHeight) {
                minHeight = fontSize * lineSpacing;
                if (listStyle) minHeight = minHeight * 3; //列表符号占2个字符

                if (height < minHeight) {
                    layer.height = minHeight;
                    isMin = true;
                }

                const measureOptions = {
                    keepConstantWidth: false,
                    keepConstantHeight: true,
                };
                const textSize = TextMeasurer.measure(layer, measureOptions);

                layer.width = textSize.width;

                return {
                    isMin,
                    width: layer.width,
                    height: layer.height,
                };
            }

            // 改变图层宽度
            minWidth = fontSize;
            if (listStyle) minWidth = minWidth * 3; //列表符号占2个字符

            if (width < minWidth) {
                layer.width = minWidth;
                isMin = true;
            }

            // 用户一旦主动操作过，宽度以用户的值为基准
            layer.__fixedDirectionSize = true;

            const measureOptions = {
                keepConstantWidth: true,
                keepConstantHeight: false,
            };
            const textSize = TextMeasurer.measure(layer, measureOptions);
            layer.height = textSize.height;

            return {
                isMin,
                width: layer.width,
                height: layer.height,
            };
        },

        /**
         * 舞台裁切操作后，对文本图层数据的额外操作
         *
         * - 主要对字体尺寸进行调整
         * - 该方法会改变 mutable 入参
         *
         * @param layer - 图层属性
         * @param scale - 缩放比例
         */
        onBeforeCrop(layer, scale) {
            const data = layer.data as ITextLayerProps;
            const { fontSize } = data;
            data.fontSize = fontSize * scale;
        },

        /**
         * 获取激活的基础信息
         *
         * @param layer - 图层属性
         */
        getActiveBaseInfo(layer: ILayerProps) {
            const layerData = layer.data as ITextLayerProps;
            const {
                fontSize,
                letterSpacing,
                textGlow = [],
                textShadow = [],
                textStroke = [],
                textBackground = [],
            } = layerData;

            return {
                fontSize,
                letterSpacing,
                textGlow: cloneDeep(textGlow),
                textShadow: cloneDeep(textShadow),
                textStroke: cloneDeep(textStroke),
                textBackground: cloneDeep(textBackground),
            };
        },

        /**
         * 获取图层缩放，图层达到最小值的比例
         *
         * @param originInfo- 原始图层信息
         * @param layer - 图层属性
         */
        getScaleMinRatio(originInfo, _) {
            const { fontSize: originFontSize = 6 } = originInfo;
            return MIN_FONT_SIZE / originFontSize;
        },

        /**
         * 获取图层的最小尺寸
         *
         * @param originInfo- 原始图层信息
         * @param layer - 图层属性
         */
        getLayerMinSize(originInfo, _) {
            const { width, height } = originInfo;
            return { width, height };
        },

        whenGroupCanResize(layer: ILayerProps) {
            const layerData = layer.data as ITextLayerProps;
            const { writingMode, shapeExtension } = layerData;

            let horizontal = false;
            let vertical = false;

            if (!shapeExtension) {
                horizontal = writingMode === TextWritingType.HORIZONTAL_TB ? true : false;
                vertical = writingMode === TextWritingType.VERTICAL_RL ? true : false;
            }
            return { horizontal, vertical };
        },

        onCheckCanResize(layer: ILayerProps) {
            const layerData = layer.data as ITextLayerProps;
            const { writingMode, shapeExtension } = layerData;

            let horizontal = false;
            let vertical = false;

            if (!shapeExtension) {
                horizontal = writingMode === TextWritingType.HORIZONTAL_TB ? true : false;
                vertical = writingMode === TextWritingType.VERTICAL_RL ? true : false;
            }
            return { horizontal, vertical };
        },

        correctLayerMinSize(width, height, ratio, originInfo) {
            const {
                width: originWidth,
                height: originHeight,
                fontSize: originFontSize = 6,
                letterSpacing: originLetterSpacing = 0,
                textGlow: originTextGlow = [],
                textShadow: originTextShadow = [],
                textStroke: originTextStroke = [],
                textBackground: originTextBackground = [],
            } = originInfo;

            let fontSize = toInteger(originFontSize * ratio);
            let letterSpacing = originLetterSpacing * ratio;
            let isMin = false;

            const resetGlow = (ratio: number) => {
                return (item: IFontGlow) => {
                    const ret = cloneDeep(item);
                    ret.width = Math.round(item.width * ratio);
                    return ret;
                };
            };

            const resetShadow = (ratio: number) => {
                return (item: IFontShadow) => {
                    const ret = cloneDeep(item);
                    ret.x = Math.round(item.x * ratio);
                    ret.y = Math.round(item.y * ratio);
                    return ret;
                };
            };

            const resetStroke = (ratio: number) => {
                return (item: IFontStroke) => {
                    const ret = cloneDeep(item);
                    ret.width = Math.round(item.width * ratio);
                    return ret;
                };
            };

            const resetBackground = (ratio: number) => {
                return (item: IFontBackground) => {
                    const ret = cloneDeep(item);
                    if (isNumber(item.roundWeight)) {
                        ret.roundWeight = Math.round(item.roundWeight * ratio);
                    }
                    return ret;
                };
            };

            let textGlow = originTextGlow.map(resetGlow(ratio));
            let textShadow = originTextShadow.map(resetShadow(ratio));
            let textStroke = originTextStroke.map(resetStroke(ratio));
            let textBackground = originTextBackground.map(resetBackground(ratio));

            // BUGFIX: https://jira.meitu.com/browse/MTXXPRO-58791
            let minOrMaxRatio: number | undefined = undefined;

            if (fontSize < MIN_FONT_SIZE) {
                fontSize = MIN_FONT_SIZE;
                minOrMaxRatio = fontSize / originFontSize;
                isMin = true;
            } else if (fontSize > MAX_FONT_SIZE) {
                fontSize = MAX_FONT_SIZE;
                minOrMaxRatio = fontSize / originFontSize;
            }

            if (typeof minOrMaxRatio !== 'undefined') {
                width = minOrMaxRatio * originWidth;
                height = minOrMaxRatio * originHeight;
                letterSpacing = minOrMaxRatio * originLetterSpacing;
                textGlow = originTextGlow.map(resetGlow(minOrMaxRatio));
                textShadow = originTextShadow.map(resetShadow(minOrMaxRatio));
                textStroke = originTextStroke.map(resetStroke(minOrMaxRatio));
                textBackground = originTextBackground.map(resetBackground(minOrMaxRatio));
            }

            return {
                width: Math.ceil(width),
                height: Math.ceil(height),
                fontSize,
                letterSpacing,
                isMin,
                textGlow,
                textShadow,
                textStroke,
                textBackground,
            };
        },

        onBeforeGetLayerWithClone(layer) {
            delete layer.__isOnTextEditing;
        },

        async onBaseScaleEnd(layerId) {
            // HACK: 文字图层通过比例计算出的尺寸不太准确，需要在缩放结束时通过测量来获取准确的尺寸

            const layer = getFramework(identity).getLayer(layerId, true);
            if (!layer) return false;

            const layerData = layer.data as ITextLayerProps;
            const { shapeExtension } = layerData;

            if (shapeExtension) return false;

            const isVertical = layerData.writingMode !== TextWritingType.HORIZONTAL_TB;

            const textSize = TextMeasurer.measure(layer, {
                keepConstantWidth: !isVertical,
                keepConstantHeight: isVertical,
            });

            const nextLayer = { ...layer, width: textSize.width, height: textSize.height };
            getFramework(identity).stage.addOrUpdate(nextLayer, false);

            return true;
        },
    };

    return behaviors;
};
